import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Trash2, Save, RefreshCw, X, Sparkles } from 'lucide-react';
import { toast } from 'react-hot-toast';

const SECTION_TITLES = ['Medya Satın Alma Dağılımı', 'Medya Satın Alma Mecraları'];
const API = 'https://api.glowytics.com';
const MONTH_OPTIONS = [
  { value: 1, label: 'Ocak' },
  { value: 2, label: 'Şubat' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' },
  { value: 5, label: 'Mayıs' },
  { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' },
  { value: 8, label: 'Ağustos' },
  { value: 9, label: 'Eylül' },
  { value: 10, label: 'Ekim' },
  { value: 11, label: 'Kasım' },
  { value: 12, label: 'Aralık' },
];

function splitSmart(line) {
  if (line.includes('\t')) return line.split('\t').map((x) => x.trim());
  return line
    .trim()
    .replace(/\s{2,}/g, '\t')
    .split('\t')
    .map((x) => x.trim());
}

/** Tablo başlık satırı (Kampanya Adı, Bütçe, Tıklama vb. — veri satırı değil) */
function isLikelyHeaderRow(parts) {
  if (parts.length < 2) return false;
  const j = parts.join(' ').toLowerCase();
  const keys = ['kampanya adı', 'bütçe', 'gösterim', 'tıklama', 'izlenme', 'kanal', 'erişim', 'erisim', 'medya satın alma'];
  const hits = keys.filter((k) => j.includes(k));
  return hits.length >= 2;
}

function normalizeHeaderCell(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ş/g, 's')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c');
}

/** Uzun eşleşme önce — başlık hücresini bilinen alana bağla; bilinmeyen → ekstra sütun */
const FIELD_ALIAS_GROUPS = [
  { key: 'channel', aliases: ['medya satin alma dagilimi', 'medya satin alma mecralari', 'kanal', 'mecra', 'format', 'dagilim'] },
  { key: 'campaign', aliases: ['kampanya adi', 'kampanya'] },
  { key: 'budget', aliases: ['butce', 'budget'] },
  { key: 'impressions', aliases: ['gosterim', 'impression'] },
  { key: 'reach', aliases: ['erisim', 'reach', 'unique reach'] },
  { key: 'clicks', aliases: ['tiklam', 'click'] },
  { key: 'views', aliases: ['izlenme', 'video view', 'views'] },
];

function matchFieldKey(headerNorm) {
  let best = null;
  let bestLen = 0;
  for (const { key, aliases } of FIELD_ALIAS_GROUPS) {
    for (const a of aliases) {
      if (headerNorm === a || headerNorm.endsWith(a) || (headerNorm.includes(a) && a.length >= 4)) {
        if (a.length >= bestLen) {
          bestLen = a.length;
          best = key;
        }
      }
    }
  }
  if (best) return best;
  for (const { key, aliases } of FIELD_ALIAS_GROUPS) {
    for (const a of aliases) {
      if (headerNorm.includes(a)) {
        if (a.length >= bestLen) {
          bestLen = a.length;
          best = key;
        }
      }
    }
  }
  return best;
}

function buildColumnMap(headerPartsRaw) {
  const used = new Set();
  return headerPartsRaw.map((raw) => {
    const label = String(raw || '').trim();
    const h = normalizeHeaderCell(label);
    const fk = matchFieldKey(h);
    if (fk && !used.has(fk)) {
      used.add(fk);
      return { kind: 'std', key: fk, label: label || fk };
    }
    return { kind: 'extra', label: label || 'Ek sütun' };
  });
}

function scoreHeaderLine(parts) {
  if (parts.length < 3) return 0;
  const j = parts.join(' ').toLowerCase();
  let score = 0;
  ['kampanya', 'bütçe', 'butce', 'gösterim', 'gosterim', 'tıklama', 'tiklam', 'izlenme', 'erişim', 'erisim', 'medya'].forEach((k) => {
    if (j.includes(k)) score += 1;
  });
  if (parts.some((p) => /kampanya|bütçe|gösterim|tıklama|izlenme|erişim/i.test(p))) score += 1;
  return score;
}

function emptyStdRow() {
  return {
    channel: '',
    campaign: '',
    budget: '',
    impressions: '',
    reach: '',
    clicks: '',
    views: '',
    extra: {},
  };
}

function rowFromMappedParts(parts, colMap) {
  const row = emptyStdRow();
  const n = colMap.length;
  let cells = parts;
  if (parts.length > n) {
    const tail = parts.slice(-(n - 2));
    const campaign = parts.slice(1, parts.length - tail.length).join(' ');
    cells = [parts[0], campaign, ...tail];
  } else if (parts.length < n) {
    cells = [...parts];
    while (cells.length < n) cells.push('');
  }
  for (let i = 0; i < colMap.length; i++) {
    const cell = (cells[i] ?? '').trim();
    const col = colMap[i];
    if (col.kind === 'std') {
      row[col.key] = cell;
    } else {
      row.extra[col.label] = cell;
    }
  }
  return row;
}

function findBestHeaderLine(lines) {
  let bestIdx = -1;
  let bestScore = 0;
  lines.forEach((line, i) => {
    const parts = splitSmart(line);
    const s = scoreHeaderLine(parts);
    if (s > bestScore && parts.length >= 4) {
      bestScore = s;
      bestIdx = i;
    }
  });
  return bestIdx >= 0 && bestScore >= 3 ? { idx: bestIdx, score: bestScore } : null;
}

function hasMoneyToken(s) {
  return /₺|€|\$|tl\b/i.test(String(s || ''));
}

/**
 * 5 sütun: Kanal | Kampanya | Bütçe | Tıklama | İzlenme (gösterim yok)
 * 6 sütun: Kanal | Kampanya | Bütçe | Gösterim | Tıklama | İzlenme
 * 7+ sütun: Kampanya alanında TAB varsa birleştirilir
 */
function parseRowFromParts(parts) {
  const n = parts.length;
  if (n < 5) return null;

  if (n === 5) {
    const [channel, campaign, budget, clicks, views] = parts;
    return { ...emptyStdRow(), channel, campaign, budget, impressions: '—', clicks, views };
  }
  if (n === 6) {
    const [channel, campaign, budget, impressions, clicks, views] = parts;
    return { ...emptyStdRow(), channel, campaign, budget, impressions, clicks, views };
  }
  if (n === 7) {
    const [channel, campaign, budget, impressions, reach, clicks, views] = parts;
    return { ...emptyStdRow(), channel, campaign, budget, impressions, reach, clicks, views };
  }

  const last5 = parts.slice(-5);
  if (n > 7 && hasMoneyToken(last5[0])) {
    const channel = parts[0];
    const budget = parts[n - 5];
    const impressions = parts[n - 4];
    const reach = parts[n - 3];
    const clicks = parts[n - 2];
    const views = parts[n - 1];
    const campaign = parts.slice(1, n - 5).join(' ').trim();
    if (!campaign) return null;
    return { ...emptyStdRow(), channel, campaign, budget, impressions, reach, clicks, views };
  }

  const last4 = parts.slice(-4);
  const last3 = parts.slice(-3);
  if (hasMoneyToken(last4[0])) {
    const channel = parts[0];
    const budget = parts[n - 4];
    const impressions = parts[n - 3];
    const clicks = parts[n - 2];
    const views = parts[n - 1];
    const campaign = parts.slice(1, n - 4).join(' ').trim();
    if (!campaign) return null;
    return { ...emptyStdRow(), channel, campaign, budget, impressions, clicks, views };
  }
  if (hasMoneyToken(last3[0])) {
    const channel = parts[0];
    const budget = parts[n - 3];
    const clicks = parts[n - 2];
    const views = parts[n - 1];
    const campaign = parts.slice(1, n - 3).join(' ').trim();
    if (!campaign) return null;
    return { ...emptyStdRow(), channel, campaign, budget, impressions: '—', clicks, views };
  }
  return null;
}

function parseSectionWithHeader(lines, title) {
  const hint = findBestHeaderLine(lines);
  if (!hint) return null;
  const headerParts = splitSmart(lines[hint.idx]);
  if (headerParts.length < 4) return null;
  const columnMap = buildColumnMap(headerParts);
  const stdKeys = columnMap.filter((c) => c.kind === 'std').map((c) => c.key);
  if (!stdKeys.includes('campaign') && !stdKeys.includes('budget')) return null;

  /** Eski Excel çıktılarında ilk sütun kanal (Display/Video) ama başlık satırı "Kampanya Adı" ile başlar — kanal/mecra yoksa legacy parse kullan */
  if (!stdKeys.includes('channel')) {
    const headerJoined = headerParts.map((h) => normalizeHeaderCell(h)).join(' ');
    const looksLikeChannelHeader =
      headerJoined.includes('medya') ||
      headerJoined.includes('mecra') ||
      headerJoined.includes('dagilim') ||
      headerJoined.includes('kanal') ||
      headerJoined.includes('format');
    if (!looksLikeChannelHeader) return null;
  }

  const rows = [];
  let total = null;
  const dataLines = lines.slice(hint.idx + 1).filter((l) => !isLikelyHeaderRow(splitSmart(l)));

  for (const line of dataLines) {
    const parts = splitSmart(line);
    if (parts.length < 2) continue;
    const row = rowFromMappedParts(parts, columnMap);
    if (!row.channel && !row.campaign) continue;
    if ((row.channel || '').toLowerCase() === 'toplam') total = row;
    else rows.push(row);
  }

  return { title, rows, total, columnMap, headerLabels: headerParts };
}

function parseSectionLegacy(lines, title) {
  const rows = [];
  let total = null;
  for (const line of lines) {
    const parts = splitSmart(line);
    if (isLikelyHeaderRow(parts)) continue;
    const row = parseRowFromParts(parts);
    if (!row) continue;
    if ((row.channel || '').toLowerCase() === 'toplam') total = row;
    else rows.push(row);
  }
  return { title, rows, total };
}

function parseSection(rawSection, title) {
  const lines = rawSection
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .filter((l) => l !== title)
    .filter((l) => !l.toLowerCase().startsWith('kampanya adı'));

  const legacy = parseSectionLegacy(lines, title);
  const withHeader = parseSectionWithHeader(lines, title);
  if (withHeader && (withHeader.rows.length > 0 || withHeader.total)) {
    return withHeader;
  }
  return legacy;
}

function parseInput(raw) {
  const text = (raw || '').replace(/\r/g, '').trim();
  if (!text) return [];

  let normalized = text;
  SECTION_TITLES.forEach((t) => {
    normalized = normalized.replaceAll(t, `\n${t}\n`);
  });

  const sections = [];
  for (const title of SECTION_TITLES) {
    const start = normalized.indexOf(title);
    if (start === -1) continue;
    const rest = normalized.slice(start + title.length);
    const nextStarts = SECTION_TITLES
      .filter((t) => t !== title)
      .map((t) => rest.indexOf(t))
      .filter((i) => i >= 0);
    const end = nextStarts.length ? Math.min(...nextStarts) : rest.length;
    const chunk = `${title}\n${rest.slice(0, end).trim()}`;
    sections.push(parseSection(chunk, title));
  }
  return sections;
}

// Gemini normalize çıktısını ({distribution, channels}) mevcut "section" şekline
// çevirir → setManualSections ile basılır, var olan "DB'ye Kaydet" akışı aynen yazar.
function normalizedToSections(norm) {
  const STD = [
    { key: 'channel', label: 'Kanal' },
    { key: 'campaign', label: 'Kampanya Adı' },
    { key: 'budget', label: 'Bütçe' },
    { key: 'impressions', label: 'Gösterim' },
    { key: 'reach', label: 'Erişim' },
    { key: 'clicks', label: 'Tıklama' },
    { key: 'views', label: 'İzlenme' },
  ];
  const toRow = (r, isTotal) => ({
    channel: isTotal ? 'Toplam' : (r.label ?? ''),
    campaign: r.campaign ?? '',
    budget: r.budget ?? null,
    impressions: r.impressions ?? null,
    reach: r.reach ?? null,
    clicks: r.clicks ?? null,
    views: r.views ?? null,
  });
  const mk = (title, table) => {
    if (!table || !Array.isArray(table.rows)) return null;
    const rows = table.rows.map((r) => toRow(r, false));
    const total = table.total ? toRow(table.total, true) : null;
    const all = [...rows, total].filter(Boolean);
    const has = (key) => ['channel', 'campaign', 'budget'].includes(key) || all.some((r) => r[key] !== null && r[key] !== '');
    const columnMap = STD.filter((c) => has(c.key)).map((c) => ({ kind: 'std', key: c.key, label: c.label }));
    return { title, rows, total, columnMap };
  };
  return [mk(SECTION_TITLES[0], norm.distribution), mk(SECTION_TITLES[1], norm.channels)].filter(Boolean);
}

function inferColumnMapFromSection(s) {
  const stdOrder = [
    { key: 'channel', label: 'Kanal' },
    { key: 'campaign', label: 'Kampanya Adı' },
    { key: 'budget', label: 'Bütçe' },
    { key: 'impressions', label: 'Gösterim' },
    { key: 'reach', label: 'Erişim' },
    { key: 'clicks', label: 'Tıklama' },
    { key: 'views', label: 'İzlenme' },
  ];
  const allRows = [...(s.rows || []), s.total].filter(Boolean);
  const extras = new Set();
  allRows.forEach((r) => {
    if (r.extra && typeof r.extra === 'object') {
      Object.keys(r.extra).forEach((k) => extras.add(k));
    }
  });
  const map = stdOrder.map(({ key, label }) => ({ kind: 'std', key, label }));
  [...extras].sort().forEach((label) => map.push({ kind: 'extra', label }));
  return map;
}

function cellValue(row, col) {
  if (!row) return '';
  if (col.kind === 'std') return row[col.key] ?? '';
  return row.extra?.[col.label] ?? '';
}

function isPlaceholderMetric(v) {
  const t = String(v ?? '').trim();
  return t === '' || t === '—' || t === '-' || t === '–';
}

function setCellOnRow(row, col, value) {
  if (!row) return;
  if (col.kind === 'std') {
    row[col.key] = value;
    return;
  }
  if (!row.extra) row.extra = {};
  row.extra[col.label] = value;
}

/**
 * Metin alanı değişince parse yenilenir; tabloda elle yapılan düzenlemeler bu state ile korunur.
 */
function applySectionCellUpdate(sections, sectionIdx, { rowType, rowIndex, col, value }) {
  const next = JSON.parse(JSON.stringify(sections));
  const sec = next[sectionIdx];
  if (!sec) return sections;
  if (rowType === 'total') {
    if (!sec.total) sec.total = emptyStdRow();
    setCellOnRow(sec.total, col, value);
  } else {
    const row = sec.rows?.[rowIndex];
    if (row) setCellOnRow(row, col, value);
  }
  return next;
}

function sectionsToText(sections) {
  if (!Array.isArray(sections) || sections.length === 0) return '';
  const out = [];
  sections.forEach((s, idx) => {
    out.push(s.title || '');
    const colMap = s.columnMap?.length ? s.columnMap : inferColumnMapFromSection(s);
    const headerLine =
      s.headerLabels && s.headerLabels.length === colMap.length
        ? s.headerLabels.join('\t')
        : colMap.map((c) => c.label).join('\t');
    out.push(headerLine);
    (s.rows || []).forEach((r) => {
      out.push(colMap.map((c) => cellValue(r, c)).join('\t'));
    });
    if (s.total) {
      out.push(colMap.map((c) => cellValue(s.total, c)).join('\t'));
    }
    if (idx < sections.length - 1) out.push('');
  });
  return out.join('\n');
}

function cellEditValue(raw) {
  const t = String(raw ?? '').trim();
  if (t === '—') return '';
  return t;
}

function MediaTable({ section, sectionIndex, onCellChange }) {
  if (!section) return null;
  const colMap = section.columnMap?.length ? section.columnMap : inferColumnMapFromSection(section);
  const minW = Math.max(920, colMap.length * 130);

  const inputClass = (isText, muted) =>
    `w-full min-w-0 bg-white border border-slate-200 rounded px-1.5 py-1 text-[12px] outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-200 ${
      isText ? 'text-left text-slate-800' : `text-right tabular-nums ${muted ? 'text-slate-500' : 'text-slate-800'}`
    }`;

  return (
    <div className="rounded-lg border border-slate-300 overflow-hidden bg-white mb-6">
      <div className="bg-[#1f4e79] px-3 py-2">
        <h3 className="text-[12px] font-bold text-white">{section.title}</h3>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse" style={{ minWidth: minW }}>
          <thead>
            <tr className="bg-[#1f4e79]">
              {colMap.map((col) => {
                const isText = col.kind === 'std' && (col.key === 'channel' || col.key === 'campaign');
                return (
                  <th
                    key={col.kind === 'std' ? col.key : col.label}
                    className={`px-3 py-2 text-[11px] font-semibold text-white border border-slate-300 ${isText ? 'text-left' : 'text-right'}`}
                  >
                    {col.label}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {section.rows.map((r, i) => (
              <tr key={`${section.title}-${r.channel}-${i}`} className={i % 2 === 0 ? 'bg-slate-50' : 'bg-white'}>
                {colMap.map((col) => {
                  const raw = cellValue(r, col);
                  const isText = col.kind === 'std' && (col.key === 'channel' || col.key === 'campaign');
                  const muted =
                    col.kind === 'std' &&
                    (col.key === 'impressions' || col.key === 'reach' || col.key === 'views') &&
                    isPlaceholderMetric(raw);
                  return (
                    <td key={col.kind === 'std' ? col.key : col.label} className="p-1 border border-slate-300 align-middle">
                      <input
                        type="text"
                        value={cellEditValue(raw)}
                        onChange={(e) => onCellChange(sectionIndex, 'row', i, col, e.target.value)}
                        className={inputClass(isText, muted)}
                        aria-label={`${section.title} satır ${i + 1} ${col.label || col.key}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
            {section.total && (
              <tr className="bg-slate-200 font-semibold">
                {colMap.map((col) => {
                  const raw = cellValue(section.total, col);
                  const isText = col.kind === 'std' && (col.key === 'channel' || col.key === 'campaign');
                  const muted =
                    col.kind === 'std' &&
                    (col.key === 'impressions' || col.key === 'reach' || col.key === 'views') &&
                    isPlaceholderMetric(raw);
                  const showToplamLabel = col.kind === 'std' && col.key === 'channel' && !String(raw || '').trim();
                  return (
                    <td key={col.kind === 'std' ? col.key : col.label} className="p-1 border border-slate-400 align-middle">
                      {showToplamLabel ? (
                        <span className="block px-1.5 py-1 text-[12px] text-slate-800">Toplam</span>
                      ) : (
                        <input
                          type="text"
                          value={cellEditValue(raw)}
                          onChange={(e) => onCellChange(sectionIndex, 'total', null, col, e.target.value)}
                          className={inputClass(isText, muted)}
                          aria-label={`${section.title} toplam ${col.label || col.key}`}
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function CustomReport6() {
  const [inputText, setInputText] = useState('');
  const [manualSections, setManualSections] = useState(null);
  const [geminiBusy, setGeminiBusy] = useState(false);
  const [geminiInfo, setGeminiInfo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [targetYear, setTargetYear] = useState(new Date().getFullYear());
  const [targetMonth, setTargetMonth] = useState(new Date().getMonth() + 1);
  const parsedSections = useMemo(() => parseInput(inputText), [inputText]);
  const sections = manualSections ?? parsedSections;

  useEffect(() => {
    setManualSections(null);
  }, [inputText]);

  const handleCellChange = useCallback(
    (sectionIdx, rowType, rowIndex, col, value) => {
      setManualSections((prev) => {
        const base = prev ?? parsedSections;
        if (!base || base.length === 0) return prev;
        return applySectionCellUpdate(base, sectionIdx, { rowType, rowIndex, col, value });
      });
    },
    [parsedSections]
  );

  const brandId = localStorage.getItem('currentBrandId');
  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
    'X-Brand-ID': brandId || '',
  }), [brandId]);

  const loadFromDb = useCallback(async () => {
    if (!brandId) return;
    try {
      setLoading(true);
      const res = await fetch(`${API}/api/brands/${brandId}/media-buying-format?year=${targetYear}&month=${targetMonth}`, {
        method: 'GET',
        credentials: 'include',
        headers,
      });
      if (!res.ok) {
        if (res.status === 403) return;
        throw new Error(`HTTP ${res.status}`);
      }
      const json = await res.json();
      if (Array.isArray(json?.sections) && json.sections.length > 0) {
        setInputText(sectionsToText(json.sections));
      } else {
        setInputText('');
      }
    } catch (e) {
      console.error('Load media buying format:', e);
      toast.error('Kayıtlı format yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, targetYear, targetMonth]);

  const runGemini = useCallback(async () => {
    if (!brandId) { toast.error('Önce bir marka seçin'); return; }
    if (!inputText.trim()) { toast.error('Önce tabloyu yapıştırın'); return; }
    try {
      setGeminiBusy(true);
      const res = await fetch(`${API}/api/brands/${brandId}/media-buying-format/normalize`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({ rawText: inputText }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json?.success) throw new Error(json?.error || `HTTP ${res.status}`);
      const secs = normalizedToSections(json);
      if (!secs.length) throw new Error('Tablo bulunamadı');
      setManualSections(secs);
      setGeminiInfo({ canonical: json.canonical, warnings: json.warnings || [], consistent: json.consistent });
      toast.success('Gemini ile anlamlandırıldı — kontrol edip "DB’ye Kaydet"e basın');
    } catch (e) {
      console.error('Gemini normalize:', e);
      toast.error(`Anlamlandırma başarısız: ${e.message || 'hata'}`);
    } finally {
      setGeminiBusy(false);
    }
  }, [brandId, headers, inputText]);

  const saveToDb = useCallback(async () => {
    if (!brandId) {
      toast.error('Önce bir marka seçin');
      return;
    }
    try {
      setSaving(true);
      const payload = { sections, reportDate: new Date().toISOString().slice(0, 10), year: targetYear, month: targetMonth };
      const res = await fetch(`${API}/api/brands/${brandId}/media-buying-format`, {
        method: 'PUT',
        credentials: 'include',
        headers,
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      toast.success('Format DB kaydı güncellendi');
      const canonical = sectionsToText(sections);
      setInputText(canonical);
      setManualSections(null);
    } catch (e) {
      console.error('Save media buying format:', e);
      toast.error(`Kaydetme başarısız: ${e.message || 'Bilinmeyen hata'}`);
    } finally {
      setSaving(false);
    }
  }, [brandId, headers, sections, targetYear, targetMonth]);

  const deleteFromDb = useCallback(async () => {
    if (!brandId) {
      toast.error('Önce bir marka seçin');
      return;
    }
    if (!window.confirm(`${targetYear} yılı ${MONTH_OPTIONS.find((m) => m.value === targetMonth)?.label || targetMonth}. ay için kayıtlı kaydı silmek istediğinize emin misiniz?`)) {
      return;
    }
    try {
      setDeleting(true);
      const res = await fetch(`${API}/api/brands/${brandId}/media-buying-format?year=${targetYear}&month=${targetMonth}`, {
        method: 'DELETE',
        credentials: 'include',
        headers,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      toast.success('Kayıt silindi');
      await loadFromDb();
    } catch (e) {
      console.error('Delete media buying format:', e);
      toast.error(`Silme başarısız: ${e.message || 'Bilinmeyen hata'}`);
    } finally {
      setDeleting(false);
    }
  }, [brandId, headers, targetYear, targetMonth, loadFromDb]);

  useEffect(() => {
    loadFromDb();
  }, [loadFromDb]);

  return (
    <div className="min-h-screen pb-12">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-800 tracking-tight">Medya Satın Alma Tablo Dönüştürücü</h1>
      </div>

      <div className="rounded-lg border border-slate-200 bg-white mb-6">
        <div className="px-3 py-2 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
          <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wider">Yapıştırma alanı</p>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(Number(e.target.value))}
              className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5 bg-white"
            >
              {[2024, 2025, 2026, 2027, 2028].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <select
              value={targetMonth}
              onChange={(e) => setTargetMonth(Number(e.target.value))}
              className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5 bg-white"
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <button
              onClick={runGemini}
              disabled={geminiBusy || !inputText.trim()}
              className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-md bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-60"
            >
              <Sparkles size={12} className={geminiBusy ? 'animate-pulse' : ''} /> {geminiBusy ? 'Anlamlandırılıyor…' : 'Gemini ile Anlamlandır'}
            </button>
            <button
              onClick={loadFromDb}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Son Kaydı Yükle
            </button>
            <button
              onClick={deleteFromDb}
              disabled={deleting}
              title={`${targetYear} / ${targetMonth}. ay kaydını sil`}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-60"
            >
              <Trash2 size={12} className={deleting ? 'animate-pulse' : ''} /> {deleting ? 'Siliniyor...' : 'Kaydı Sil'}
            </button>
            <button
              onClick={saveToDb}
              disabled={saving || sections.length === 0}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-slate-800 text-white hover:bg-slate-700 disabled:opacity-60"
            >
              <Save size={12} /> {saving ? 'Kaydediliyor...' : 'DB’ye Kaydet'}
            </button>
            <button
              onClick={() => setInputText('')}
              className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50"
            >
              <X size={12} /> Input Temizle
            </button>
          </div>
        </div>
        <div className="p-3">
          <textarea
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            placeholder=""
            className="w-full h-56 text-[12px] border border-slate-200 rounded-md p-3 outline-none focus:border-slate-400 font-mono"
          />
        </div>
      </div>

      {geminiInfo && (
        <div className={`mb-4 rounded-lg border px-4 py-3 ${geminiInfo.consistent ? 'border-emerald-200 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`}>
          <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
            <Sparkles size={14} className="text-violet-500" />
            Kanonik Toplam (rapora BİR KEZ işlenecek): ₺{Number(geminiInfo.canonical?.budget || 0).toLocaleString('tr-TR')}
            <span className="text-[11px] font-normal text-slate-500">
              kaynak: {geminiInfo.canonical?.source === 'channels' ? 'Mecra (detay)' : 'Dağılım'}
            </span>
          </div>
          {!geminiInfo.consistent && (geminiInfo.warnings || []).length > 0 && (
            <ul className="mt-1.5 list-disc pl-5 text-[11px] text-amber-700">
              {geminiInfo.warnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          )}
        </div>
      )}

      {sections.length === 0 ? null : (
        sections.map((s, idx) => (
          <MediaTable key={`${s.title}-${idx}`} section={s} sectionIndex={idx} onCellChange={handleCellChange} />
        ))
      )}
    </div>
  );
}

